import React from "react";
/* Input — Phase B primitive.
   Wraps native <input> with token-driven styling, optional prefix/suffix.

   size: sm (28h) | md (32h, default)
   kind: text | number | password | email | search | tel | date
   prefix / suffix: ReactNode (e.g. "€" / "%")
   error: string | null — applies .v2-input--error + aria-invalid
*/

export default function Input({
  size = "md",
  kind = "text",
  value,
  onChange,
  disabled = false,
  error = null,
  prefix,
  suffix,
  rawEvent = false,
  className = "",
  id,
  ...rest
}) {
  const handleChange = (e) => {
    if (typeof onChange !== "function") return;
    onChange(rawEvent ? e : e.target.value);
  };
  // Phase 4b audit — className now composes with the v2-input classes so
  // callers can add layout/decorator classes (e.g. v2-input--cell for table
  // cells, v2-pw-gate__input for the password gate) without having to
  // wrap Input in another element. Phase B left className on the wrap-only
  // path, which silently dropped it for the bare-input path.
  const inputCls = [
    "v2-input",
    `v2-input--${size}`,
    error ? "v2-input--error" : "",
    (prefix || suffix) ? "v2-input--has-affix" : "",
    className,
  ].filter(Boolean).join(" ");
  const errorId = error && id ? `${id}-error` : undefined;
  const inputEl = (
    <input
      type={kind}
      value={value == null ? "" : value}
      onChange={handleChange}
      disabled={disabled}
      className={inputCls}
      id={id}
      aria-invalid={error ? true : undefined}
      aria-describedby={errorId}
      {...rest}
    />
  );
  if (!prefix && !suffix && !error) return inputEl;
  return (
    // Wrap chrome class chain doesn't include the caller's className (which
    // already applied to the input itself above); keeps wrap chrome free of
    // bespoke styling.
    <span className={`v2-input-wrap v2-input-wrap--${size}`.trim()}>
      {prefix != null ? <span className="v2-input__prefix">{prefix}</span> : null}
      {inputEl}
      {suffix != null ? <span className="v2-input__suffix">{suffix}</span> : null}
      {error && (
        <span id={errorId} className="v2-input__error" role="alert">{error}</span>
      )}
    </span>
  );
}
